import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  insertNewFlightTemplateNodes,
  INewTemplateReturn,
  getFlightBuilderTemplates,
  updateFileTemplate,
  setActiveFileTemplate,
} from "src/redux/actions/FlightBuilder.Actions";
import { useTheme } from "@mui/styles";
import { Flights } from "./Flights";
import { RecurringFlights } from "./RecurringFlights";
import { GroundEvents } from "./GroundEvents";
import { FileTreeContainer } from "./FileTreeContainer";
import classNames from "classnames";
import { DialogModal } from "src/components/DialogModal/DialogModal";
import { AlertModel } from "src/redux/state/Alert";
import { Grid, Typography, Drawer, Theme, Button, Tooltip } from "@mui/material";
import ArrowTip from "./icons/arrow-tip.svg?react";
import { IFlightTemplate, IFlightType } from "@vmosolutions/oc-interfaces";
import { IFlightBuilder } from "src/redux/reducers/FlightBuilderReducer";
// Display Build status including any potential overlap conflicts with existing flights.
import { BuildStatus, IBuildPayload, IUpdateTableEvents } from "./BuildStatus/BuildStatus";
import { TextField } from "src/components/FormikComponents/TextField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import moment from "moment-timezone";
import { IconButton } from "src/components/IconButton/IconButton";
import BackspaceIcon from "@mui/icons-material/Backspace";

export type EventTypes = "Flight" | "RecurrentFlight" | "GroundEvent";
interface IFlightBuilderProps {
  id: string;
  path: string[];
  windowProps?: {
    eventType: EventTypes;
    // setFocusOnEvent?: (event: IFlight | IGroundEvent) => void;
  };
}

const FlightBuilderStyles = (theme: Theme): any => ({
  color: "white",
  flexWrap: "nowrap",
  overflow: "auto",
  height: "100%",
  "& .FlightBuilder": {
    display: "flex",
    flexDirection: "column",
    width: "calc(100% - 16px)",
    margin: "5px 8px",
  },
  "& .fileTreeDrawer": {
    position: "relative",
    width: 0,
    padding: "10px 0px 0px 0px",
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflowX: "hidden",
    overflowY: "auto",
    borderRight: `1px solid ${theme.custom.forms.inputBorder}`,
  },
  "& .fileTreeDrawerOpen": {
    width: 238,
    transition: theme.transitions.create(["width", "border"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  "& .fileTreeDrawerClosed": {
    width: 42,
    transition: theme.transitions.create(["width", "border"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
  },
  "& .fileTreeDrawerHidden": {
    width: 0,
    overflowX: "hidden",
    borderColor: theme.palette.background.default,
  },
  "& .fileTreeDrawerPaper": {
    height: "100%",
    width: 238,
    position: "relative",
    visibility: "visible !important" as "visible", // using type assertion to fix typescript error.
    overflowX: "hidden",
    transform: "translateX(0px) !important",
    border: "none",
  },
  "& .fileTreeOpenSpace": {
    display: "flex",
  },
  "& .fileTemplateContainer": {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexWrap: "nowrap",
    flexShrink: 1,
    overflowY: "auto",
    position: "relative", // set relative so that Build Status tool can use as ref.
    "& .form_title_container": {
      padding: "15px 0px 0px 15px",
      maxWidth: 360,
      "& .description": {
        fontSize: 14,
        fontWeight: 300,
        color: theme.custom.forms.inputText,
        maxWidth: 700,
      },
    },
    "& .file_template_actions": {
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
      "& .template_filename": {
        width: 332,
        "& .input_control label": { width: "auto" },
      },
    },
    "& .build_status_container": {
      width: "calc(100% - 360px)",
      height: 160,
    },
  },
  "& .fieldItem": {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    margin: "5px",
    flexWrap: "wrap",
    "&.select_event_type": {
      "& .input_control": {
        "& label": { width: 86 }, // sized so that is matches template name field
        "& .react-select-container": { width: 236 }, // sized so that is matches template name field
      },
    },
  },
  "& .arrowButtons": {
    margin: "6px 8px",
    "&.MuiButton-text": { padding: 0 },
    "& .button_icon": {
      width: 32,
      height: 32,
      "& path": { fill: theme.palette.primary.main },
    },
    "& .button_label": {
      display: "flex",
      height: 32,
      alignItems: "center",
      padding: "0px 10px",
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      borderRadius: "0px 3px 3px 0px",
      lineHeight: "14px",
      "& .warning": {
        margin: "0px 5px",
        fontSize: 12,
        fontWeight: 300,
        textTransform: "lowercase",
      },
    },
    // conditional style when icon is placed on the right
    "&.icon_right": {
      "& .button_label": { borderRadius: "3px 0px 0px 3px" },
    },
    "&.flip_icon": {
      "& .button_icon": { transform: "scaleX(-1)" },
    },
    "&.no_icon": {
      "& .button_label": { borderRadius: "3px 3px 3px 3px" },
    },
    "&.Mui-disabled": { opacity: 0.5 },
    // Disable standard button effects and replace with our own.
    "& .MuiTouchRipple-root": { display: "none !important" },
    "&:hover": {
      backgroundColor: "transparent !important",
      "& .button_icon": {
        "& path": {
          fill: theme.palette.primary.dark,
        },
      },
      "& .button_label": {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
});

/**
 * FlightBuilder has a fileTree of flight templates the user can choose from.
 * Once a template is chosen the right "Template" view displays the base flight creation,
 * and fills out what it can based on the template.
 * @param props - Props for the FlightBuilder
 */

const eventTypeOptions = [
  { value: "Flight", label: "Flights" },
  { value: "RecurrentFlight", label: "Recurring Flights" },
  { value: "GroundEvent", label: "Ground Events" },
  // { value: 'RecurrentEvent', label: 'Recurring Event(s)' }
];

export const FlightBuilder = (props: IFlightBuilderProps): JSX.Element => {
  const { id, path, windowProps } = props;
  const { eventType } = windowProps;

  const theme: Theme = useTheme();
  //const classes = useStyles(theme);
  const dispatch = useDispatch();
  const isLoaded = useSelector((state: IRootState) => state.flightBuilderTemplates.isLoaded);

  // Template File Browser
  const [fileTreeOpen, setfileTreeOpen] = useState<boolean>(true);
  const [fileTreeHidden, setfileTreeHidden] = useState<boolean>(false);

  const flightBuilderTemplates: IFlightBuilder = useSelector((state: IRootState) => state.flightBuilderTemplates);
  const { activeNode, activeFileTemplate } = flightBuilderTemplates;

  // If activeNode isn't a leafNode and activeFileTemplate is null, user has folder selected.
  const isFolder = activeNode && !activeNode?.isLeafNode && !activeFileTemplate;

  const [templateName, setTemplateName] = useState<string>(null);
  const [templateChanges, setTemplateChanges] = useState<IFlightTemplate>(null);

  const [mosaicPaneId, setMosaicPaneId] = useState<string>(null);
  const [eventTypeOption, setEventTypeOption] = useState<any>(eventTypeOptions[0]);
  const [dialogData, setDialogData] = useState<any>(null);
  const [buildStatusPayload, setBuildStatusPayload] = useState<IBuildPayload>(null);

  // Store an object containig an array of all successfully created flights containing their new id anmd old temp.
  const [updateTableEvents, setUpdateTableEvents] = useState<IUpdateTableEvents>(null);
  const [startDate, setStartDate] = useState<number>(moment.utc(new Date()).startOf("day")?.valueOf());
  const [buildAsLocal, setBuildAsLocal] = useState<boolean>(false);

  useEffect(() => {
    // Set default time mode for given builder event types (only affects flights/recurring flights)
    // Recurring flights follow their template's mode (synced in RecurringFlights).
    if (eventTypeOption?.value === "Flight") {
      setBuildAsLocal(false);
    } else if (eventTypeOption?.value !== "RecurrentFlight") {
      setBuildAsLocal(true);
    }
  }, [eventTypeOption]);

  //default event types
  const customerStructure = useSelector((state: IRootState) => state.structures.customerStructure);
  const { flightTypes, globalAppConfig } = customerStructure;
  const defaultFlightTypeId: string = useMemo(
    () => globalAppConfig?.customer?.settings?.defaultFlightTypeId,
    [globalAppConfig]
  );
  const defaultFlightType: IFlightType = useMemo(() => {
    if (flightTypes && defaultFlightTypeId) {
      return flightTypes.find((option) => option?._id === defaultFlightTypeId);
    } else return undefined;
  }, [defaultFlightTypeId, flightTypes]);

  const defaultGroundEventTypeId: string = useMemo(
    () => globalAppConfig?.customer?.settings?.defaultGroundEventTypeId,
    [globalAppConfig]
  );
  const defaultGroundEventType: IFlightType = useMemo(() => {
    if (flightTypes && defaultGroundEventTypeId) {
      return flightTypes.find((option) => option?._id === defaultGroundEventTypeId);
    } else return undefined;
  }, [defaultGroundEventTypeId, flightTypes]);

  // Default the event builder in a specific mode if passed from the windowProps on mount.
  useEffect(() => {
    if (eventType) {
      const loadEvent = eventTypeOptions.filter((option: any) => option.value === eventType)[0];
      if (loadEvent) setEventTypeOption(loadEvent);
    }
  }, [eventType]);

  // Once an id has been passed, get the mosaicWindow to verify that the pane exists before passing Id.
  useEffect(() => {
    if (id) {
      const mosaicWindow = document.getElementById(id + "-panel");
      if (mosaicWindow) setMosaicPaneId(id + "-panel");
    }
  }, [id]);

  // Effect to load templates
  useEffect(() => {
    if (!isLoaded) void dispatch(getFlightBuilderTemplates());
  }, [isLoaded, dispatch]);

  // Effect on change of event type
  useEffect(() => {
    if (
      !!eventTypeOption &&
      (eventTypeOption.value === "RecurrentFlight" || eventTypeOption.value === "RecurrentEvent")
    ) {
      setfileTreeHidden(false);
    } else {
      setfileTreeHidden(true);
    }
  }, [eventTypeOption]);

  const templateChangedState = useCallback((updatedTemplate: IFlightTemplate): void => {
    setTemplateChanges(updatedTemplate);
    setBuildStatusPayload(updatedTemplate);
  }, []);

  useEffect(() => {
    // if activeFileTemplate changes, reset these
    setTemplateChanges(null);
    setTemplateName(activeFileTemplate?.fileName);
  }, [activeFileTemplate]);

  // SAVE CHANGES currently loaded template.
  const saveTemplateChanges = useCallback((): void => {
    const fileName = templateName ?? activeFileTemplate?.fileName;
    const newTemplate = templateChanges ? { ...templateChanges, fileName } : { ...activeFileTemplate, fileName };
    dispatch(updateFileTemplate(newTemplate));
    // once saved set the change detections back to null.
    setTemplateChanges(null);
    setTemplateName(null);
  }, [dispatch, templateChanges, templateName, activeFileTemplate]);

  // SAVE A NEW template in the currently active node (folder).
  const saveAsNewTemplate = useCallback(async (): Promise<void> => {
    const fileName = templateName ?? activeFileTemplate?.fileName;
    const path = activeFileTemplate
      ? activeFileTemplate.isLeafNode
        ? activeFileTemplate.path
        : [...activeFileTemplate.path, activeFileTemplate._id]
      : [];
    const newTemplateFile: IFlightTemplate = {
      fileName: fileName,
      path: [],
      isLeafNode: true,
      operator: templateChanges?.operator,
      rRule: templateChanges?.rRule,
      asLocal: templateChanges?.asLocal,
      pairingPositionCounts: templateChanges?.pairingPositionCounts,
      legs: templateChanges?.legs ?? [],
      includeSplits: templateChanges?.includeSplits,
    };
    const response: INewTemplateReturn = await insertNewFlightTemplateNodes([newTemplateFile], path, dispatch);
    if (response.newTemplates && Array.isArray(response.newTemplates) && response.newTemplates?.length > 0) {
      // If call was successfull, set the newly created node as active.
      dispatch(setActiveFileTemplate(response.newTemplates[0]));
    }
  }, [templateChanges, templateName, activeFileTemplate, dispatch]);

  //  * Change Creation Type used in the builder component. (checks to see if forms are dirty before switching)
  //  */
  const changeEventType = (type: any): void => {
    const oldType = eventTypeOption.label;
    let currentFormDirty;
    switch (eventTypeOption.value) {
      default:
        currentFormDirty = false;
    }
    if (currentFormDirty) {
      const dialogData = new AlertModel({
        title: `Change event Type to ${type.label}?`,
        message: `You have unsaved changed in the ${oldType} creation form, these will be lost if you switch event type`,
        buttons: [
          {
            label: `Return to ${oldType} form`,
            handler: () => {
              setDialogData(null);
            },
          },
          {
            label: "Change Type",
            handler: () => {
              setEventTypeOption(type);
              setBuildStatusPayload(null);
              setDialogData(null);
            },
          },
        ],
      });
      setDialogData(dialogData);
    } else {
      setEventTypeOption(type);
      setBuildStatusPayload(null);
    }
  };

  return (
    <Grid container direction="row" wrap="nowrap" sx={{ ...FlightBuilderStyles(theme), flexBasis: 0, flexGrow: 1 }}>
      <Drawer
        className={classNames(
          "fileTreeDrawer",
          fileTreeOpen && "fileTreeDrawerOpen",
          !fileTreeOpen && "fileTreeDrawerClosed",
          fileTreeHidden && "fileTreeDrawerHidden"
        )}
        classes={{ paper: "fileTreeDrawerPaper" }}
        hidden={false}
        variant="persistent"
        open={fileTreeOpen}
      >
        <FileTreeContainer fileTreeOpen={fileTreeOpen} toggleFileTreeDrawer={() => setfileTreeOpen(!fileTreeOpen)} />
      </Drawer>

      <Grid item className={"fileTemplateContainer"}>
        <Grid container direction="row" justifyContent="space-between" wrap="nowrap">
          <Grid item className="form_title_container">
            <Typography className="title" variant="h5">
              Timeline Event Builder
            </Typography>

            <SelectField
              className={classNames("fieldItem", "select_event_type")}
              name="buildEventType"
              label="Event Type"
              isMulti={false}
              onChange={(value: any) => changeEventType(value)}
              value={eventTypeOption}
              options={eventTypeOptions ?? []}
              menuPosition="fixed"
              isSearchable={true}
              isClearable={false}
            />

            {eventTypeOption?.value === "RecurrentFlight" && (
              <Grid container className="file_template_actions">
                <Grid item sx={{ width: 250 }}>
                  <TextField
                    className="template_filename"
                    name="templateName"
                    label="Template Name"
                    value={templateName ?? activeFileTemplate?.fileName}
                    onChange={(event) => setTemplateName(event?.target?.value ?? null)}
                    meta={{
                      initialValue: activeFileTemplate?.fileName,
                      touched: templateName && activeFileTemplate?.fileName !== templateName,
                    }}
                  />
                </Grid>
                {activeFileTemplate ? (
                  <>
                    <Button
                      sx={{ marginTop: 15 }}
                      id="save_template_changes_btn"
                      className={"arrowButtons"}
                      onClick={() => saveTemplateChanges()}
                      disabled={!templateChanges && activeFileTemplate?.fileName === templateName}
                    >
                      <ArrowTip className="button_icon" />
                      <div className="button_label">Save Template Changes</div>
                    </Button>
                    {activeFileTemplate?._id && (
                      <IconButton
                        className="deselect_btn"
                        onClick={() => dispatch(setActiveFileTemplate(null))}
                        sx={{ borderRadius: "3px", height: "32px", margin: "5px" }}
                        tooltipProps={{ title: "Deselect active template" }}
                      >
                        <BackspaceIcon />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <Tooltip
                    title={`Will create a new template ${
                      isFolder ? `in the "${activeNode?.fileName ?? ""}" folder` : ""
                    }`}
                    placement="top"
                    enterDelay={500}
                  >
                    <span>
                      <Button
                        sx={{ marginTop: "15px" }}
                        id="create_new_template_btn"
                        className={classNames("arrowButtons", "no_icon")}
                        onClick={() => saveAsNewTemplate()}
                        disabled={!templateName}
                      >
                        <div className="button_label">{`Save New Template ${(isFolder ?? "") && "to Folder"}`}</div>
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Grid>
            )}
          </Grid>
          <Grid item className="build_status_container">
            <BuildStatus
              mosaicId={id}
              eventType={eventTypeOption}
              payload={buildStatusPayload}
              setUpdateTableEvents={setUpdateTableEvents}
              startDate={startDate}
              setStartDate={setStartDate}
              buildAsLocal={buildAsLocal}
            />
          </Grid>
        </Grid>
        {(() => {
          switch (eventTypeOption.value) {
            case "Flight":
              return (
                <Flights
                  id={mosaicPaneId}
                  path={path}
                  eventType={eventTypeOption}
                  // setFocusOnEvent={setFocusOnEvent}
                  sendBuildStatus={setBuildStatusPayload}
                  updateTableEvents={updateTableEvents}
                  defaultFlightType={defaultFlightType}
                  startDate={startDate}
                  buildAsLocal={buildAsLocal}
                  setBuildAsLocal={setBuildAsLocal}
                />
              );
            case "RecurrentFlight":
              return (
                <RecurringFlights
                  id={mosaicPaneId}
                  path={path}
                  eventType={eventTypeOption}
                  openFileTreeDrawer={() => setfileTreeOpen(!fileTreeOpen)}
                  fileTreeOpen={fileTreeOpen}
                  // setFocusOnEvent={setFocusOnEvent}
                  sendBuildStatus={setBuildStatusPayload}
                  templateChangedState={templateChangedState}
                  updateTableEvents={updateTableEvents}
                  defaultFlightType={defaultFlightType}
                  startDate={startDate}
                  buildAsLocal={buildAsLocal}
                  setBuildAsLocal={setBuildAsLocal}
                  setDialogData={setDialogData}
                />
              );
            case "GroundEvent":
              return (
                <GroundEvents
                  id={mosaicPaneId}
                  path={path}
                  eventType={eventTypeOption}
                  // setFocusOnEvent={setFocusOnEvent}
                  sendBuildStatus={setBuildStatusPayload}
                  updateTableEvents={updateTableEvents}
                  defaultGroundEventType={defaultGroundEventType}
                />
              );
            default:
              return null;
          }
        })()}

        <DialogModal data={dialogData} />
      </Grid>
    </Grid>
  );
};
